import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useLazyQuery } from "@apollo/client";
import { FiInfo, FiExternalLink, FiMusic } from "react-icons/fi";
import { IoCheckmarkSharp } from "react-icons/io5";
import { TbChevronDown } from "react-icons/tb";

import theme from "theme/themeThree";
import { Button } from "MVVM/Components/Button";
import { InputText } from "MVVM/Components/Inputs";
import { STUDIO_QUERY } from "MVVM/GraphQL/studioFacet";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { CompactCardGrid } from "MVVM/Views/Studio/Components/MainRail/Components/CardGrid";
import { ReactComponent as StudioBadgeIcon } from "../../../../assets/images/badges/studio_icon.svg";
import { ReactComponent as StickmanAnimRaw } from "./stickman-anim.svg";

const StickmanAnim = React.memo(StickmanAnimRaw);
import { IAmsCard, ICustomSeverity } from "../@types";
import {
  buildSingleEventKeyframes,
  outputToInput,
  totalOutputDuration,
  Keyframe,
} from "./timeRemap";

const BEAT_FREQUENCY_OPTIONS = [
  { label: "1/1", value: 1.0 },
  { label: "1/2", value: 0.5 },
  { label: "1/4", value: 0.25 },
  { label: "1/8", value: 0.125 },
];

const MAX_VELOCITY_OPTIONS = [10, 15, 20, 25, 30, 35, 40].map((v) => ({
  label: `${v}x`,
  value: v,
}));

const SEVERITY_PRESETS: { label: string; values: ICustomSeverity }[] = [
  {
    label: "Standard",
    values: {
      lead: 0.5,
      hit: 0.55,
      post: 0.2,
      lead_duration: 0.1,
      hit_duration: 0.1,
      post_duration: 0.7,
    },
  },
  {
    label: "Heavy Hitter",
    values: {
      lead: 0.3,
      hit: 0.5,
      post: 0.0,
      lead_duration: 0.35,
      hit_duration: 0.2,
      post_duration: 0.8,
    },
  },
  {
    label: "Final Words",
    values: {
      lead: 0.0,
      hit: 0.5,
      post: 0.5,
      lead_duration: 0.65,
      hit_duration: 0.05,
      post_duration: 0.3,
    },
  },
];

const matchesPreset = (a: ICustomSeverity, b: ICustomSeverity) =>
  a.lead === b.lead &&
  a.hit === b.hit &&
  a.post === b.post &&
  a.lead_duration === b.lead_duration &&
  a.hit_duration === b.hit_duration &&
  a.post_duration === b.post_duration;

const SVG_EVENT_INPUT = 1.4833333;
const SVG_TOTAL = 4.4833333;
const VISUALIZATION_BEAT_PERIOD = 4.0;
const LOOP_PAUSE = 0.4;

const formatSpeedSeverity = (v: number) => {
  if (v <= 0) return "Max slow";
  if (v >= 1) return "Max fast";
  const slowPct = Math.round((0.5 - v) * 200);
  const fastPct = Math.round((v - 0.5) * 200);
  if (slowPct === 0 && fastPct === 0) return "Realtime";
  return slowPct > 0 ? `${slowPct}% slow` : `${fastPct}% fast`;
};

interface IFragmovieSettingsProps {
  onReset: () => void;
  selectedSong: IAmsCard | null;
  setSelectedSong: (song: IAmsCard | null) => void;
  songVolume: number;
  setSongVolume: (volume: number) => void;
  customVolumeEnabled: boolean;
  setCustomVolumeEnabled: (enabled: boolean) => void;
  impactSeverity: number;
  setImpactSeverity: (severity: number) => void;
  customSeverityEnabled: boolean;
  setCustomSeverityEnabled: (enabled: boolean) => void;
  customSeverity: ICustomSeverity;
  setCustomSeverity: (s: ICustomSeverity) => void;
  limitSlowmo: boolean;
  setLimitSlowmo: (v: boolean) => void;
  eventBreathingRoom: boolean;
  setEventBreathingRoom: (v: boolean) => void;
  speedupSyncEnabled: boolean;
  setSpeedupSyncEnabled: (v: boolean) => void;
  speedupMaxVelocity: number;
  setSpeedupMaxVelocity: (v: number) => void;
  speedupBeatFrequency: number;
  setSpeedupBeatFrequency: (v: number) => void;
  speedupVfxEnabled: boolean;
  setSpeedupVfxEnabled: (enabled: boolean) => void;
  applyStudioCards: boolean;
  setApplyStudioCards: (apply: boolean) => void;
  showSongError: boolean;
  setShowSongError: (show: boolean) => void;
}

// ── Reusable primitives ──────────────────────────────────────────────────────

interface TabSelectorProps {
  options: { label: string; value: string }[];
  value: string;
  onChange: (v: string) => void;
}

const TabSelector = ({ options, value, onChange }: TabSelectorProps) => (
  <TabSelectorWrapper>
    {options.map((opt) => (
      <TabOption
        key={opt.value}
        $active={opt.value === value}
        onClick={() => onChange(opt.value)}
      >
        {opt.label}
      </TabOption>
    ))}
  </TabSelectorWrapper>
);

interface MiniSliderProps {
  value: number;
  onChange: (v: number) => void;
  onRelease?: () => void;
  step?: number;
  min?: number;
  max?: number;
  format?: (v: number) => string;
}

const MiniSlider = ({
  value,
  onChange,
  onRelease,
  step = 0.05,
  min = 0,
  max = 1,
  format = (v) => `${Math.round(v * 100)}%`,
}: MiniSliderProps) => (
  <MiniSliderCell>
    <MiniSliderValue>{format(value)}</MiniSliderValue>
    <MiniSliderInput
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      onPointerUp={onRelease}
      onKeyUp={onRelease}
    />
  </MiniSliderCell>
);

interface SnapSliderProps {
  options: { label: string; value: number }[];
  value: number;
  onChange: (v: number) => void;
}

const SnapSlider = ({ options, value, onChange }: SnapSliderProps) => {
  const idx = options.findIndex((o) => o.value === value);
  const activeIdx = idx === -1 ? 0 : idx;
  const lastIdx = Math.max(1, options.length - 1);
  // Thumb is 16px (see Slider styled-component); thumb center travels from 8px to (width-8px).
  const labelLeft = (i: number) => `calc(8px + ${i / lastIdx} * (100% - 16px))`;
  return (
    <SnapSliderWrapper>
      <Slider
        type="range"
        min={0}
        max={options.length - 1}
        step={1}
        value={activeIdx}
        onChange={(e) => onChange(options[Number(e.target.value)].value)}
      />
      <SnapLabels>
        {options.map((opt, i) => (
          <SnapLabel
            key={opt.value}
            $active={i === activeIdx}
            style={{ left: labelLeft(i) }}
          >
            {opt.label}
          </SnapLabel>
        ))}
      </SnapLabels>
    </SnapSliderWrapper>
  );
};

interface InlineCollapsibleProps {
  title: string;
  tooltip?: React.ReactNode;
  rightContent?: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  children: React.ReactNode;
}

const InlineCollapsible = ({
  title,
  tooltip,
  rightContent,
  defaultExpanded = true,
  expanded: controlledExpanded,
  onToggle,
  children,
}: InlineCollapsibleProps) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;
  const handleToggle = () => {
    if (isControlled) onToggle?.();
    else setInternalExpanded(!internalExpanded);
  };
  return (
    <ToggleColumn>
      <CollapseHeader onClick={handleToggle}>
        <Flex alignItems="center" style={{ gap: 8, flex: 1, minWidth: 0 }}>
          <CollapseChevron $expanded={expanded}>
            <TbChevronDown size={18} />
          </CollapseChevron>
          <CollapseTitle>{title}</CollapseTitle>
          {tooltip}
        </Flex>
        {rightContent && (
          <div onClick={(e) => e.stopPropagation()}>{rightContent}</div>
        )}
      </CollapseHeader>
      {expanded && <CollapseBody>{children}</CollapseBody>}
    </ToggleColumn>
  );
};

// ── Main component ───────────────────────────────────────────────────────────

export const FragmovieSettings = ({
  onReset,
  selectedSong,
  setSelectedSong,
  songVolume,
  setSongVolume,
  customVolumeEnabled,
  setCustomVolumeEnabled,
  impactSeverity,
  setImpactSeverity,
  customSeverityEnabled,
  setCustomSeverityEnabled,
  customSeverity,
  setCustomSeverity,
  limitSlowmo,
  setLimitSlowmo,
  eventBreathingRoom,
  setEventBreathingRoom,
  speedupSyncEnabled,
  setSpeedupSyncEnabled,
  speedupMaxVelocity,
  setSpeedupMaxVelocity,
  speedupBeatFrequency,
  setSpeedupBeatFrequency,
  speedupVfxEnabled,
  setSpeedupVfxEnabled,
  applyStudioCards,
  setApplyStudioCards,
  showSongError,
  setShowSongError,
}: IFragmovieSettingsProps) => {
  const [songSelectionOpen, setSongSelectionOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [themeFilter, setThemeFilter] = useState("");
  const [pendingSelection, setPendingSelection] = useState<IAmsCard | null>(
    null,
  );
  const [hoveredSongId, setHoveredSongId] = useState<string | null>(null);
  const [speedupSyncExpanded, setSpeedupSyncExpanded] =
    useState(speedupSyncEnabled);
  const [stickmanEl, setStickmanEl] = useState<SVGSVGElement | null>(null);
  const stickmanRef = useCallback((node: SVGSVGElement | null) => {
    setStickmanEl(node);
  }, []);
  const keyframesRef = useRef<Keyframe[]>([]);
  const stickmanHoverRef = useRef(false);
  const playbackStartRef = useRef(0);
  const cycleStartOutRef = useRef(0);
  const lastInputRef = useRef(0);
  const lastAppliedRef = useRef(-1);

  const resetPlayback = useCallback(() => {
    playbackStartRef.current = performance.now();
    cycleStartOutRef.current = 0;
    lastInputRef.current = 0;
    lastAppliedRef.current = -1;
    if (stickmanEl) {
      try {
        stickmanEl.setCurrentTime(0);
      } catch {}
    }
  }, [stickmanEl]);

  useEffect(() => {
    keyframesRef.current = buildSingleEventKeyframes({
      eventInTs: SVG_EVENT_INPUT,
      beatPeriod: VISUALIZATION_BEAT_PERIOD,
      severity: Math.max(0, Math.min(1, impactSeverity / 100)),
      customSeverity: customSeverityEnabled ? customSeverity : null,
      clipInEnd: SVG_TOTAL,
    });
  }, [
    impactSeverity,
    customSeverityEnabled,
    customSeverity.lead,
    customSeverity.hit,
    customSeverity.post,
    customSeverity.lead_duration,
    customSeverity.hit_duration,
    customSeverity.post_duration,
  ]);

  useEffect(() => {
    const svg = stickmanEl;
    if (!svg) return;

    svg.pauseAnimations();
    let rafId = 0;
    let running = false;
    let visible = true;
    let docVisible = !document.hidden;
    let pausedAt = 0;

    const applyTime = (t: number) => {
      if (Math.abs(t - lastAppliedRef.current) < 0.001) return;
      lastAppliedRef.current = t;
      svg.setCurrentTime(t);
    };

    const tick = (now: number) => {
      const elapsedOut = (now - playbackStartRef.current) / 1000;
      if (stickmanHoverRef.current) {
        const cycle = SVG_TOTAL + LOOP_PAUSE;
        applyTime(Math.min(elapsedOut % cycle, SVG_TOTAL));
      } else {
        const keyframes = keyframesRef.current;
        if (keyframes.length > 0) {
          const totalOut = totalOutputDuration(keyframes);
          const cycle = totalOut + LOOP_PAUSE;
          if (elapsedOut - cycleStartOutRef.current >= cycle) {
            cycleStartOutRef.current = elapsedOut;
            lastInputRef.current = 0;
          }
          const outT = elapsedOut - cycleStartOutRef.current;
          const target =
            outT >= totalOut ? SVG_TOTAL : outputToInput(outT, keyframes);
          const inT =
            target < lastInputRef.current ? lastInputRef.current : target;
          lastInputRef.current = inT;
          applyTime(inT);
        }
      }
      rafId = window.requestAnimationFrame(tick);
    };

    const start = () => {
      if (running) return;
      running = true;
      if (pausedAt > 0) {
        playbackStartRef.current += performance.now() - pausedAt;
        pausedAt = 0;
      } else {
        playbackStartRef.current = performance.now();
        cycleStartOutRef.current = 0;
        lastInputRef.current = 0;
        lastAppliedRef.current = -1;
      }
      rafId = window.requestAnimationFrame(tick);
    };

    const stop = () => {
      if (!running) return;
      running = false;
      pausedAt = performance.now();
      window.cancelAnimationFrame(rafId);
    };

    const sync = () => {
      if (visible && docVisible) start();
      else stop();
    };

    const io = new IntersectionObserver(
      (entries) => {
        visible = entries[0]?.isIntersecting ?? false;
        sync();
      },
      { threshold: 0 },
    );
    io.observe(svg);

    const onVisChange = () => {
      docVisible = !document.hidden;
      sync();
    };
    document.addEventListener("visibilitychange", onVisChange);

    start();

    return () => {
      stop();
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisChange);
      stickmanHoverRef.current = false;
      try {
        svg.unpauseAnimations();
        svg.setCurrentTime(0);
      } catch {}
    };
  }, [stickmanEl]);

  const [fetchSongs, { data: songsData, loading: songsLoading }] =
    useLazyQuery(STUDIO_QUERY);

  useEffect(() => {
    if (songSelectionOpen) {
      fetchSongs({
        variables: {
          type: ["Music"],
          theme: themeFilter ? [themeFilter] : undefined,
        },
      });
    }
  }, [songSelectionOpen, themeFilter, fetchSongs]);

  const songs: IAmsCard[] = songsData?.facets?.cards || [];
  const themeFacets = songsData?.facets?.facets?.find(
    (f: { identifier: string }) => f.identifier === "theme",
  );
  const filteredSongs = songs.filter((song) =>
    song.ui.title.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleChooseSong = () => {
    if (pendingSelection) {
      setSelectedSong(pendingSelection);
      setPendingSelection(null);
      setShowSongError(false);
      setSongSelectionOpen(false);
    }
  };

  const setSeverityField = (field: keyof ICustomSeverity, value: number) =>
    setCustomSeverity({ ...customSeverity, [field]: value });

  const displayVolume = customVolumeEnabled ? songVolume : 40;

  const SEVERITY_COLS = [
    {
      label: "Pre-kill",
      speedField: "lead" as keyof ICustomSeverity,
      durField: "lead_duration" as keyof ICustomSeverity,
    },
    {
      label: "On-kill",
      speedField: "hit" as keyof ICustomSeverity,
      durField: "hit_duration" as keyof ICustomSeverity,
    },
    {
      label: "Post-kill",
      speedField: "post" as keyof ICustomSeverity,
      durField: "post_duration" as keyof ICustomSeverity,
    },
  ];

  return (
    <>
      <SettingsContainer>
        {/* ── MUSIC ── */}
        <CategoryHeader>Music</CategoryHeader>

        <ToggleColumn>
          <ToggleRow>
            <Flex alignItems="center" style={{ gap: 4 }}>
              <Text variant="text.labelSmall">Song Selection</Text>
            </Flex>
            {showSongError && <SongErrorText>required</SongErrorText>}
          </ToggleRow>
          <SongSelectButton
            $highlight={!selectedSong}
            onClick={() => setSongSelectionOpen(true)}
          >
            {selectedSong ? (
              <span>{selectedSong.ui.title}</span>
            ) : (
              <SongPlaceholder>
                <FiMusic size={14} />
                Choose a song...
              </SongPlaceholder>
            )}
          </SongSelectButton>
          {!selectedSong && (
            <SongHint>
              Select a song to configure the rest of your fragmovie
            </SongHint>
          )}
        </ToggleColumn>

        <DisabledOverlay $disabled={!selectedSong}>
          <ToggleColumn>
            <ToggleRow>
              <Flex alignItems="center" style={{ gap: 4 }}>
                <Text variant="text.labelSmall">Music volume</Text>
                <AnimatedTooltip
                  content="Sets how loud the music is vs clip audio. 0 = clip audio only, 100 = music only."
                  position="top"
                >
                  <InfoIcon />
                </AnimatedTooltip>
                {customVolumeEnabled && (
                  <ResetInline
                    onClick={() => {
                      setCustomVolumeEnabled(false);
                      setSongVolume(40);
                    }}
                  >
                    Reset
                  </ResetInline>
                )}
              </Flex>
              <SettingValue>{displayVolume}%</SettingValue>
            </ToggleRow>
            <SliderContainer>
              <SliderLabels>
                <span>Clip audio only</span>
                <span>Music only</span>
              </SliderLabels>
              <Slider
                type="range"
                min={0}
                max={100}
                value={displayVolume}
                onChange={(e) => {
                  setCustomVolumeEnabled(true);
                  setSongVolume(Number(e.target.value));
                }}
              />
            </SliderContainer>
          </ToggleColumn>

          {/* ── TIME FLOW ── */}
          <CategoryHeader>Time Flow</CategoryHeader>

          <InlineCollapsible
            title="Impact"
            tooltip={
              <AnimatedTooltip
                content={
                  <>
                    When the kill and the music align just perfectly...
                    <br />
                    <br />
                    <b>Simple</b>: How dramatic should we make the slow motion
                    effect?
                    <br />
                    <br />
                    <b>Custom</b>: Fine-tune how you like your kills to hit.
                  </>
                }
                position="top"
              >
                <InfoIcon />
              </AnimatedTooltip>
            }
            rightContent={
              <TabSelector
                options={[
                  { label: "Simple", value: "simple" },
                  { label: "Custom", value: "custom" },
                ]}
                value={customSeverityEnabled ? "custom" : "simple"}
                onChange={(v) => {
                  setCustomSeverityEnabled(v === "custom");
                  resetPlayback();
                }}
              />
            }
          >
            {!customSeverityEnabled && (
              <SliderContainer>
                <SimpleSliderHeader>
                  <SimpleSliderLabel>Impact Intensity</SimpleSliderLabel>
                  <SimpleSliderValue>
                    {impactSeverity === 0
                      ? "No impact"
                      : impactSeverity === 100
                      ? "Super slowmo"
                      : `${impactSeverity}%`}
                  </SimpleSliderValue>
                </SimpleSliderHeader>
                <SliderLabelsLg>
                  <span>No impact</span>
                  <span>Super slowmo</span>
                </SliderLabelsLg>
                <Slider
                  type="range"
                  min={0}
                  max={100}
                  value={impactSeverity}
                  onChange={(e) => setImpactSeverity(Number(e.target.value))}
                  onPointerUp={resetPlayback}
                  onKeyUp={resetPlayback}
                />
              </SliderContainer>
            )}

            {customSeverityEnabled && (
              <SubsectionInner>
                <PresetsRow>
                  <PresetsLabel>Presets</PresetsLabel>
                  <PresetButtons>
                    {SEVERITY_PRESETS.map((preset) => (
                      <PresetButton
                        key={preset.label}
                        $active={matchesPreset(customSeverity, preset.values)}
                        onClick={() => {
                          setCustomSeverity(preset.values);
                          resetPlayback();
                        }}
                      >
                        {preset.label}
                      </PresetButton>
                    ))}
                  </PresetButtons>
                </PresetsRow>
                <SeverityTable>
                  <span />
                  {SEVERITY_COLS.map(({ label }) => (
                    <SeverityColHeader key={label}>{label}</SeverityColHeader>
                  ))}

                  <SeverityRowLabel>Speed</SeverityRowLabel>
                  {SEVERITY_COLS.map(({ label, speedField }) => (
                    <MiniSlider
                      key={`speed-${label}`}
                      value={customSeverity[speedField]}
                      onChange={(v) => setSeverityField(speedField, v)}
                      onRelease={resetPlayback}
                      format={formatSpeedSeverity}
                    />
                  ))}

                  <SeverityRowLabel>Duration</SeverityRowLabel>
                  {SEVERITY_COLS.map(({ label, durField }) => (
                    <MiniSlider
                      key={`dur-${label}`}
                      value={customSeverity[durField]}
                      onChange={(v) => setSeverityField(durField, v)}
                      onRelease={resetPlayback}
                    />
                  ))}
                </SeverityTable>
              </SubsectionInner>
            )}
            <StickmanAnimBlock>
              <StickmanAnimWrapper>
                <StickmanPreviewLabel>Preview</StickmanPreviewLabel>
                <StickmanAnim ref={stickmanRef} />
              </StickmanAnimWrapper>
              <StickmanHoverHint
                onMouseEnter={() => {
                  stickmanHoverRef.current = true;
                  resetPlayback();
                }}
                onMouseLeave={() => {
                  stickmanHoverRef.current = false;
                  resetPlayback();
                }}
              >
                hover here to see original speed
              </StickmanHoverHint>
            </StickmanAnimBlock>
          </InlineCollapsible>

          <ToggleColumn>
            <ToggleRow>
              <Flex alignItems="center" style={{ gap: 4 }}>
                <Text variant="text.labelSmall">Limit slowmo</Text>
                <AnimatedTooltip
                  content={
                    <>
                      Prevents heavier slowmo between events that happen close
                      together
                      <br />
                      <i>
                        Use this with higher slow-motion values for better
                        results.
                      </i>
                    </>
                  }
                  position="top"
                >
                  <InfoIcon />
                </AnimatedTooltip>
              </Flex>
              <Checkbox
                $checked={limitSlowmo}
                onClick={() => setLimitSlowmo(!limitSlowmo)}
              >
                {limitSlowmo && <IoCheckmarkSharp size={12} />}
              </Checkbox>
            </ToggleRow>
          </ToggleColumn>

          <ToggleColumn>
            <ToggleRow>
              <Flex alignItems="center" style={{ gap: 4 }}>
                <Text variant="text.labelSmall">Extra Breathing Room</Text>
                <AnimatedTooltip
                  content="Adds some extra real-time segments before and after fast-forward sections."
                  position="top"
                >
                  <InfoIcon />
                </AnimatedTooltip>
              </Flex>
              <Checkbox
                $checked={eventBreathingRoom}
                onClick={() => setEventBreathingRoom(!eventBreathingRoom)}
              >
                {eventBreathingRoom && <IoCheckmarkSharp size={12} />}
              </Checkbox>
            </ToggleRow>
          </ToggleColumn>

          <InlineCollapsible
            title="Fast-Forward Sync"
            tooltip={
              <AnimatedTooltip
                content={
                  <>
                    Do multiple smaller fast-forwards instead of one that skips
                    more time.
                    <br />
                    Syncs fast-forward to the music.
                  </>
                }
                position="top"
              >
                <InfoIcon />
              </AnimatedTooltip>
            }
            expanded={speedupSyncExpanded}
            onToggle={() => setSpeedupSyncExpanded(!speedupSyncExpanded)}
            rightContent={
              <Checkbox
                $checked={speedupSyncEnabled}
                onClick={() => {
                  const next = !speedupSyncEnabled;
                  setSpeedupSyncEnabled(next);
                  setSpeedupSyncExpanded(next);
                }}
              >
                {speedupSyncEnabled && <IoCheckmarkSharp size={12} />}
              </Checkbox>
            }
          >
            <Flex alignItems="center" style={{ gap: 4, marginBottom: 6 }}>
              <SectionLabel>Max velocity</SectionLabel>
              <AnimatedTooltip
                content={
                  <>
                    Controls when to trigger Speedup Sync and how fast the clip
                    can proceed.
                    <br />
                    Smaller values make the resulting clip longer as less time
                    is skipped at once.
                  </>
                }
                position="top"
              >
                <InfoIcon />
              </AnimatedTooltip>
            </Flex>
            <SnapSlider
              options={MAX_VELOCITY_OPTIONS}
              value={speedupMaxVelocity}
              onChange={setSpeedupMaxVelocity}
            />
            <Flex
              alignItems="center"
              style={{ gap: 4, marginTop: 18, marginBottom: 6 }}
            >
              <SectionLabel>Beat frequency</SectionLabel>
              <AnimatedTooltip
                content="The rythm frequency for fast-forwarding."
                position="top"
              >
                <InfoIcon />
              </AnimatedTooltip>
            </Flex>
            <SnapSlider
              options={BEAT_FREQUENCY_OPTIONS}
              value={speedupBeatFrequency}
              onChange={setSpeedupBeatFrequency}
            />
          </InlineCollapsible>

          {/* ── No group header ── */}
          <ToggleColumn>
            <ToggleRow>
              <Flex alignItems="center" style={{ gap: 4 }}>
                <Text variant="text.labelSmall">Speedup VFX</Text>
                <AnimatedTooltip
                  content="Adds a flash effect synced with the speed of the clip"
                  position="top"
                >
                  <InfoIcon />
                </AnimatedTooltip>
              </Flex>
              <Checkbox
                $checked={speedupVfxEnabled}
                onClick={() => setSpeedupVfxEnabled(!speedupVfxEnabled)}
              >
                {speedupVfxEnabled && <IoCheckmarkSharp size={12} />}
              </Checkbox>
            </ToggleRow>
          </ToggleColumn>

          <ToggleColumn>
            <ToggleRow>
              <Flex alignItems="center" style={{ gap: 4 }}>
                <StyledStudioIcon />
                <Text variant="text.labelSmall">Use Studio cards</Text>
                <AnimatedTooltip
                  content={
                    <>
                      Applies your enabled Studio cards (intros, outros,
                      ReShade, watermarks, thumbnails). Music, cameras,
                      transitions, and memes are not applicable.
                    </>
                  }
                  position="top"
                >
                  <InfoIcon />
                </AnimatedTooltip>
                <GoToStudioLink
                  href="/studio"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FiExternalLink size={12} />
                </GoToStudioLink>
              </Flex>
              <Checkbox
                $checked={applyStudioCards}
                onClick={() => setApplyStudioCards(!applyStudioCards)}
              >
                {applyStudioCards && <IoCheckmarkSharp size={12} />}
              </Checkbox>
            </ToggleRow>
          </ToggleColumn>
        </DisabledOverlay>

        <ResetRow>
          <ResetLink onClick={onReset}>Reset fragmovie settings</ResetLink>
        </ResetRow>
      </SettingsContainer>

      {songSelectionOpen && (
        <SongSelectionOverlay>
          <SongSelectionContainer>
            <SongSelectionHeader>
              <Text variant="text.headingXSmall">Select a Song</Text>
            </SongSelectionHeader>

            <SearchAndFilterRow>
              <SearchInput
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                placeholder="Search songs..."
              />
              {themeFacets && (
                <StyledSelect
                  value={themeFilter}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setThemeFilter(e.target.value)
                  }
                >
                  <option value="">All Themes</option>
                  {themeFacets.entries?.map(
                    (entry: { label: string; count: number }) => (
                      <option key={entry.label} value={entry.label}>
                        {entry.label} ({entry.count})
                      </option>
                    ),
                  )}
                </StyledSelect>
              )}
            </SearchAndFilterRow>

            <SongsGridWrapper>
              {songsLoading ? (
                <LoadingContainer>
                  <LoadingSpinner height={30} width={30} />
                </LoadingContainer>
              ) : filteredSongs.length === 0 ? (
                <NoSongsText>No songs found</NoSongsText>
              ) : (
                <CompactCardGrid
                  cards={filteredSongs}
                  selectedCardId={pendingSelection?._id}
                  onCardSelect={(song: IAmsCard) => setPendingSelection(song)}
                  hoveredCardId={hoveredSongId}
                  onCardHover={setHoveredSongId}
                />
              )}
            </SongsGridWrapper>

            <SongSelectionFooter>
              <FooterLeftSection>
                <Button
                  classification="roundedFilledAlt"
                  size="small"
                  onClick={() => {
                    setPendingSelection(null);
                    setSongSelectionOpen(false);
                  }}
                  flex={true}
                >
                  Back
                </Button>
                <SelectionIndicator>
                  {pendingSelection ? (
                    <>
                      Selected:{" "}
                      <SelectedSongTitle>
                        {pendingSelection.ui.title}
                      </SelectedSongTitle>
                    </>
                  ) : (
                    <NoSelectionText>No song selected</NoSelectionText>
                  )}
                </SelectionIndicator>
              </FooterLeftSection>
              <Button
                classification="roundedFilled"
                size="small"
                onClick={handleChooseSong}
                disabled={!pendingSelection}
                flex={true}
              >
                Choose Song
              </Button>
            </SongSelectionFooter>
          </SongSelectionContainer>
        </SongSelectionOverlay>
      )}
    </>
  );
};

// ── Styled Components ────────────────────────────────────────────────────────

const SettingsContainer = styled(Box)`
  width: 100%;
`;

const ResetRow = styled(Flex)`
  justify-content: flex-end;
  padding: 6px 16px 0;
`;

const ResetLink = styled(Text)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  cursor: pointer;
  font-size: 11px;
  transition: color 150ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const ResetInline = styled(Text)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  cursor: pointer;
  font-size: 10px;
  text-decoration: underline;
  transition: color 150ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const DisabledOverlay = styled.div<{ $disabled: boolean }>`
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
  transition: opacity 200ms ease;
`;

const StyledStudioIcon = styled(StudioBadgeIcon)`
  height: 16px;
  width: 16px;
`;

const GoToStudioLink = styled.a`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  margin-left: 4px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CategoryHeader = styled(Text)`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
  display: flex;
  font-size: 15px;
  font-weight: 700;
  gap: 6px;
  letter-spacing: 1.5px;
  padding: 16px 16px 6px;
  text-transform: uppercase;
`;

const CollapseHeader = styled(Flex)`
  align-items: center;
  cursor: pointer;
  justify-content: space-between;
  user-select: none;
  width: 100%;
`;

const CollapseChevron = styled(Box)<{ $expanded: boolean }>`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  display: flex;
  flex-shrink: 0;
  transform: rotate(${({ $expanded }) => ($expanded ? "0deg" : "-90deg")});
  transition: transform 150ms ease;
`;

const CollapseTitle = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  font-weight: 600;
`;

const CollapseBody = styled(Box)`
  margin-top: 12px;
  width: 100%;
`;

const SectionLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  font-weight: 500;
`;

const SliderLabelsLg = styled(Box)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  display: flex;
  font-size: 12px;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const StickmanAnimBlock = styled(Box)`
  margin-top: 36px;
  text-align: right;
  width: 100%;
`;

const StickmanAnimWrapper = styled(Box)`
  background: ${({ theme }) => theme.colors.midnightAlpha40};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  position: relative;
  width: 100%;

  svg {
    contain: content;
    display: block;
    height: auto;
    transform: translateZ(0);
    width: 100%;
    will-change: transform;
  }

  svg .seg {
    stroke: ${({ theme }) => theme.colors.envy};
  }

  svg .head {
    fill: ${({ theme }) => theme.colors.envy};
  }
`;

const StickmanPreviewLabel = styled(Text)`
  background: ${({ theme }) => theme.colors.darkerNed};
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  font-size: 11px;
  font-weight: 600;
  left: 10px;
  letter-spacing: 0.8px;
  line-height: 1;
  padding: 3px 8px;
  position: absolute;
  text-transform: uppercase;
  top: 1px;
  transform: translateY(-100%);
`;

const StickmanHoverHint = styled(Text)`
  color: ${({ theme }) => theme.colors.chalkAlpha40};
  cursor: help;
  display: inline-block;
  font-size: 11px;
  font-style: italic;
  margin-top: 4px;

  &:hover {
    color: ${({ theme }) => theme.colors.chalkAlpha60};
  }
`;

const SimpleSliderHeader = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  width: 100%;
`;

const SimpleSliderLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  font-weight: 500;
`;

const SimpleSliderValue = styled(Text)`
  color: ${({ theme }) => theme.colors.envy};
  font-size: 12px;
  font-weight: 600;
`;

const SubsectionInner = styled(Box)`
  border-left: 2px solid ${({ theme }) => theme.colors.envyAlpha40};
  margin-top: 10px;
  padding-left: 12px;
  width: 100%;
`;

// Presets row

const PresetsRow = styled(Flex)`
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  width: 100%;
`;

const PresetsLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const PresetButtons = styled(Flex)`
  flex: 1;
  flex-wrap: wrap;
  gap: 6px;
`;

const PresetButton = styled(Text)<{ $active: boolean }>`
  background: ${({ $active, theme }) =>
    $active ? theme.colors.envy : theme.colors.chalkAlpha10};
  border-radius: 999px;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.darkerNed : theme.colors.chalkAlpha60};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  transition:
    background 150ms ease,
    color 150ms ease;
  white-space: nowrap;

  &:hover {
    background: ${({ $active, theme }) =>
      $active ? theme.colors.envy : theme.colors.chalkAlpha20};
    color: ${({ $active, theme }) =>
      $active ? theme.colors.darkerNed : theme.colors.chalk};
  }
`;

// Severity custom grid

const SeverityTable = styled(Box)`
  align-items: center;
  display: grid;
  grid-column-gap: 6px;
  grid-row-gap: 12px;
  grid-template-columns: 70px 1fr 1fr 1fr;
  width: 100%;
`;

const SeverityColHeader = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 2px;
  text-align: center;
`;

const SeverityRowLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

// MiniSlider (compact slider with value above)

const MiniSliderCell = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  width: 100%;
`;

const MiniSliderValue = styled(Text)`
  color: ${({ theme }) => theme.colors.envy};
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
`;

const MiniSliderInput = styled.input`
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;
  height: 19px;
  margin: -8px 0;
  outline: none;
  padding: 0;
  width: 100%;

  &::-webkit-slider-runnable-track {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 2px;
    height: 3px;
  }

  &::-moz-range-track {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 2px;
    height: 3px;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    background: ${({ theme }) => theme.colors.envy};
    border-radius: 50%;
    cursor: pointer;
    height: 12px;
    margin-top: -4.5px;
    width: 12px;
  }

  &::-moz-range-thumb {
    background: ${({ theme }) => theme.colors.envy};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    height: 12px;
    width: 12px;
  }
`;

// TabSelector

const TabSelectorWrapper = styled(Flex)`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 999px;
  gap: 0;
  overflow: hidden;
  padding: 2px;
`;

const TabOption = styled(Text)<{ $active: boolean }>`
  background: ${({ $active, theme }) =>
    $active ? theme.colors.envy : "transparent"};
  border-radius: 999px;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.darkerNed : theme.colors.chalkAlpha60};
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 12px;
  transition:
    background 150ms ease,
    color 150ms ease;
  white-space: nowrap;

  &:hover {
    color: ${({ $active, theme }) =>
      $active ? theme.colors.darkerNed : theme.colors.chalk};
  }
`;

// SnapSlider

const SnapSliderWrapper = styled(Box)`
  width: 100%;
`;

const SnapLabels = styled(Box)`
  height: 18px;
  margin-top: 6px;
  position: relative;
  width: 100%;
`;

const SnapLabel = styled(Text)<{ $active: boolean }>`
  color: ${({ $active, theme }) =>
    $active ? theme.colors.envy : theme.colors.chalkAlpha60};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  position: absolute;
  text-align: center;
  top: 0;
  transform: translateX(-50%);
  white-space: nowrap;
`;

// Standard controls

const ToggleColumn = styled(Flex)`
  align-items: flex-start;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  flex-direction: column;
  justify-content: space-between;
  padding: 10px 16px;
  width: 100%;

  &:last-child {
    border-bottom: none;
  }
`;

const ToggleRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const InfoIcon = styled(FiInfo)`
  color: ${({ theme }) => theme.colors.chalkAlpha60};
  cursor: help;
  flex-shrink: 0;
  height: 14px;
  width: 14px;
`;

const SettingValue = styled(Box)`
  color: ${({ theme }) => theme.colors.envy};
  font-size: 12px;
  font-weight: 600;
`;

const SongErrorText = styled(Box)`
  color: ${({ theme }) => theme.colors.caution};
  font-size: 12px;
  font-weight: 500;
`;

const SongSelectButton = styled(Box)<{ $highlight?: boolean }>`
  background: ${({ theme }) => theme.colors.grey825};
  border: 1px solid
    ${({ $highlight, theme }) =>
      $highlight ? theme.colors.envyAlpha40 : theme.colors.chalkAlpha20};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-size: 14px;
  margin-top: 8px;
  padding: 12px 16px;
  text-align: left;
  width: 100%;

  ${({ $highlight }) =>
    $highlight && `animation: songPulse 2s ease-in-out infinite;`}

  @keyframes songPulse {
    0%,
    100% {
      border-color: rgba(78, 189, 148, 0.2);
    }
    50% {
      border-color: rgba(78, 189, 148, 0.5);
    }
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
    animation: none;
  }
`;

const SongHint = styled(Text)`
  color: ${({ theme }) => theme.colors.caution};
  font-size: 11px;
  margin-top: 6px;
`;

const SliderContainer = styled(Box)`
  margin-top: 8px;
  width: 100%;
`;

const SliderLabels = styled(Box)`
  color: ${({ theme }) => theme.colors.chalkAlpha40};
  display: flex;
  font-size: 10px;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const Slider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;
  height: 20px;
  margin: -8px 0;
  outline: none;
  padding: 0;
  width: 100%;

  &::-webkit-slider-runnable-track {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 2px;
    height: 4px;
  }

  &::-moz-range-track {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 2px;
    height: 4px;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    background: ${({ theme }) => theme.colors.envy};
    border-radius: 50%;
    cursor: pointer;
    height: 16px;
    margin-top: -6px;
    width: 16px;
  }

  &::-moz-range-thumb {
    background: ${({ theme }) => theme.colors.envy};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    height: 16px;
    width: 16px;
  }
`;

const SongPlaceholder = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalkAlpha40};
  display: flex;
  gap: 8px;
`;

const Checkbox = styled.div<{ $checked: boolean }>`
  align-items: center;
  background-color: ${({ $checked, theme }) =>
    $checked ? theme.colors.envy : "transparent"};
  border: 2px solid
    ${({ $checked, theme }) =>
      $checked ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 4px;
  color: white;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 18px;
  justify-content: center;
  transition: all 150ms ease;
  width: 18px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

const StyledSelect = styled.select`
  background: ${({ theme }) => theme.colors.grey825};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  font-size: 13px;
  padding: 4px 8px;

  &:focus {
    border-color: ${({ theme }) => theme.colors.envy};
    outline: none;
  }
`;

// Song selection modal

const SongSelectionOverlay = styled(Box)`
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
`;

const SongSelectionContainer = styled(Flex)`
  background: ${({ theme }) => theme.colors.darkerNed};
  border-radius: 12px;
  flex-direction: column;
  max-height: 80vh;
  max-width: 600px;
  width: 90%;
`;

const SongSelectionHeader = styled(Box)`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  display: flex;
  justify-content: left;
  padding: 24px;
`;

const SearchAndFilterRow = styled(Box)`
  display: flex;
  gap: 12px;
  padding: 16px 24px;
`;

const SearchInput = styled(InputText)`
  ${theme.text.labelSmall}
  background-color: ${({ theme }) => theme.colors.grey825};
  border: none;
  flex: 1;

  ::placeholder {
    color: ${({ theme }) => theme.colors.chalkAlpha40};
  }
`;

const SongsGridWrapper = styled(Box)`
  flex: 1;
  max-height: 300px;
  overflow-y: auto;
  padding: 0 24px;

  scrollbar-color: ${({ theme }) => theme.colors.chalkAlpha20} transparent;
  scrollbar-width: thin;

  ::-webkit-scrollbar {
    width: 8px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 4px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha40};
  }
`;

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

const NoSongsText = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  padding: 40px;
  text-align: center;
`;

const SongSelectionFooter = styled(Box)`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  display: flex;
  justify-content: space-between;
  padding: 12px 24px;
`;

const FooterLeftSection = styled(Box)`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const SelectionIndicator = styled(Box)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  display: flex;
  font-size: 13px;
  gap: 4px;
`;

const SelectedSongTitle = styled(Box)`
  color: ${({ theme }) => theme.colors.envy};
  font-weight: 600;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const NoSelectionText = styled(Box)`
  color: ${({ theme }) => theme.colors.chalkAlpha40};
  font-style: italic;
`;
